import { Box, Slide, SxProps } from '@mui/material';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { H, V } from '../../../Layout';
import { useEntitySecurityPath } from '../../../auth/Security';
import { useDeepLinkRuntimeState } from '../../../deepLink';
import { SIDE_PANEL_ID_TO_LINKED_ENTITY_NODE_KEY_MAP } from '../../../deepLink/constants';
import { deepLinkLog } from '../../../deepLink/ui/utils';
import { useIsWithinBreakpoints } from '../../../hooks/useIsWithinBreakpoints';
import type { SecuredActionType } from '../../ActionType';
import { MainSidePanelProps, SecuredSidePanelDef, SidePanelDef } from '../LayoutUtils';
import { SidePanelToolbar } from '../SidePanelToolbar';
import { useSidePanelSx } from './WithSidePanel';

export type SidePanelContentProps = Omit<MainSidePanelProps, 'entitySecurityProviderProps'>;

export default function SidePanelContent({
    securedPanels,
    leftToolbar,
    children,
    visibleSidePanelId,
    hideHeader,
    panelBoxSx = {},
    rightToolbar,
    expandView,
    enableDeepLink = false
}: SidePanelContentProps) {
    const sidePanelSx: SxProps = useSidePanelSx(expandView);
    const isSmallScreen = useIsWithinBreakpoints(['xs', 's']);

    const entitySecurityPath = useEntitySecurityPath();

    deepLinkLog('group', `SidePanelContent[${entitySecurityPath}] Rendering.`);

    const { getDeepLinkRuntimeState, setDeepLinkRuntimeState, clearDeepLinkRuntimeState } = useDeepLinkRuntimeState();

    const [visibleSidePanel, setVisibleSidePanel] = useState<SidePanelDef | undefined>(undefined);

    const markPanelOpenInStore = useCallback(
        (panelId: string) => {
            if (!enableDeepLink) {
                return;
            }

            const panelNodePath = getUIStatePathForPanelId(entitySecurityPath, panelId);
            if (!panelNodePath) {
                return;
            }

            const existingNode = getDeepLinkRuntimeState(panelNodePath);

            setDeepLinkRuntimeState(panelNodePath, existingNode || true);
        },
        [enableDeepLink, entitySecurityPath, getDeepLinkRuntimeState, setDeepLinkRuntimeState]
    );

    const clearPanelOpenInStore = useCallback(
        (panelId: string) => {
            if (!enableDeepLink) {
                return;
            }

            const panelNodePath = getUIStatePathForPanelId(entitySecurityPath, panelId);
            if (!panelNodePath) {
                return;
            }

            clearDeepLinkRuntimeState(panelNodePath);
        },
        [clearDeepLinkRuntimeState, enableDeepLink, entitySecurityPath]
    );

    const setVisibleSidePanelWithSync = useCallback(
        (panel: SidePanelDef | undefined) => {
            setVisibleSidePanel((prevVisibleSidePanel) => {
                const prevPanelId = prevVisibleSidePanel?.id;
                const newPanelId = panel?.id;

                const panelActionType: 'switch' | 'close' | 'open' | undefined =
                    prevPanelId && newPanelId && prevPanelId !== newPanelId
                        ? 'switch'
                        : !newPanelId
                          ? 'close'
                          : newPanelId && !prevPanelId
                            ? 'open'
                            : undefined;

                switch (panelActionType) {
                    case 'switch':
                        deepLinkLog(
                            'debug',
                            `Switching visible panel from "${prevPanelId}" to "${newPanelId}". Switching in store accordingly.`
                        );
                        clearPanelOpenInStore(prevPanelId);
                        markPanelOpenInStore(newPanelId);
                        break;
                    case 'close':
                        deepLinkLog('debug', `Closing visible panel "${prevPanelId}". Clearing it from store.`);
                        clearPanelOpenInStore(prevPanelId);
                        break;
                    case 'open':
                        deepLinkLog('debug', `Opening visible panel "${newPanelId}". Marking it as open in store.`);
                        markPanelOpenInStore(newPanelId);
                        break;
                    default:
                        deepLinkLog('debug', `No change in open/close state.`);
                }

                return panel;
            });
        },
        [clearPanelOpenInStore, markPanelOpenInStore]
    );

    useEffect(() => {
        deepLinkLog(
            'group',
            `SidePanelContent[${entitySecurityPath}]->useEffect->visibleSidePanelId prop: ${visibleSidePanelId}`
        );

        if (!securedPanels) {
            setVisibleSidePanelWithSync(undefined);
            deepLinkLog('groupEnd');
            return;
        }

        if (visibleSidePanelId != null) {
            const panelFromProp = securedPanels.find((p) => p.id === visibleSidePanelId);

            deepLinkLog(
                'info',
                `Next visible side panel id: "${visibleSidePanelId}". Set based on visibleSidePanelId prop.`
            );

            if (panelFromProp) {
                markPanelOpenInStore(visibleSidePanelId);
            }

            setVisibleSidePanelWithSync(panelFromProp);

            deepLinkLog('groupEnd');
            return;
        }

        // Keep the currently-visible panel only while it still exists in the active panels list.
        setVisibleSidePanel((prevVisibleSidePanel) => {
            if (!prevVisibleSidePanel) {
                return undefined;
            }

            const matchingExistingPanel = securedPanels.find((panel) => panel.id === prevVisibleSidePanel.id);

            if (!matchingExistingPanel) {
                deepLinkLog(
                    'info',
                    `Previously visible panel id "${prevVisibleSidePanel.id}" is no longer available. Closing panel.`
                );
                return undefined;
            }

            return prevVisibleSidePanel;
        });

        deepLinkLog('groupEnd');
    }, [entitySecurityPath, markPanelOpenInStore, securedPanels, setVisibleSidePanelWithSync, visibleSidePanelId]);

    deepLinkLog('groupEnd');

    // Normalize panel open/close and async onClick activation in one place.
    const mapPanelToSecuredAction = useCallback(
        (panel: SecuredSidePanelDef): SecuredActionType => {
            const activatePanel = () =>
                setVisibleSidePanelWithSync(visibleSidePanel && visibleSidePanel.id === panel.id ? undefined : panel);

            return {
                icon: panel.icon,
                label: panel.label,
                selected: visibleSidePanel && visibleSidePanel.id === panel.id,
                tooltip: panel.tooltip,
                disabled: panel.disabled,
                hidden: panel.hidden,
                securityAction: panel.securityAction,
                featureName: panel.featureName,
                entityName: panel.entityName,
                entitySecurity: panel.entitySecurity,
                onClick: async () => {
                    if (panel.onClick) {
                        await panel.onClick();
                    }

                    activatePanel();
                }
            };
        },
        [setVisibleSidePanelWithSync, visibleSidePanel]
    );

    const securedActions = useMemo(
        () => (securedPanels || []).map(mapPanelToSecuredAction),
        [mapPanelToSecuredAction, securedPanels]
    );

    return (
        <V className="withSidePanel" fill sx={{ gap: 1, overflow: 'hidden' }}>
            {hideHeader ? (
                <Fragment />
            ) : (
                <H className="withSidePanel_top">
                    <H grow>{leftToolbar}</H>
                    <H sx={{ pr: 1, gap: 2, alignItems: 'center', justifyContent: 'flex-end', width: 'unset' }}>
                        <H>{rightToolbar}</H>
                        <SidePanelToolbar securedActions={securedActions} />
                    </H>
                </H>
            )}
            <H className="withSidePanel_bottom" grow sx={{ gap: 1, overflow: 'hidden' }}>
                {visibleSidePanel && isSmallScreen ? <></> : children}
                <Slide direction="left" in={!!visibleSidePanel} mountOnEnter unmountOnExit>
                    <Box
                        data-interactive-overlay
                        sx={{
                            height: '100%',
                            overflow: 'hidden',
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            ...sidePanelSx,
                            ...panelBoxSx
                        }}
                    >
                        {visibleSidePanel &&
                            visibleSidePanel.component(() => {
                                setVisibleSidePanelWithSync(undefined);
                            })}
                    </Box>
                </Slide>
            </H>
        </V>
    );
}

function getUIStatePathForPanelId(basePath: string, panelId: string): string | undefined {
    if (!basePath) {
        return undefined;
    }

    const mappedLinkedEntityNodeKey = SIDE_PANEL_ID_TO_LINKED_ENTITY_NODE_KEY_MAP[panelId];

    if (!mappedLinkedEntityNodeKey) {
        console.warn(
            `No mapped linked entity node key found for panel id "${panelId}" in getUIStatePathForPanelId function in SidePanelContent.`
        );
        return undefined;
    }

    const panelNodePath = `${basePath}.${mappedLinkedEntityNodeKey}`;

    return panelNodePath;
}
